import * as React from "react";
import { api } from "@/lib/api";
import { fmtCents } from "@/lib/time";
import { History, Calendar as CalendarIcon } from "lucide-react";

/**
 * AttributeHistoryPanel — dense timeline of every effective-dated
 * change on the person record.
 *
 * Rows are grouped by attribute so a reader can scan "how did their
 * rate move" without visual clutter.  Mono numerals throughout — this
 * panel is the audit trail.
 */
const ATTR_LABEL = {
  rate: "Rate",
  manager: "Manager",
  site_home: "Home site",
  job_title: "Job title",
  contract_type: "Contract",
  employment_status: "Status",
  fte_hours: "FTE hours",
  continuous_service_date: "Continuous svc",
  probation_end: "Probation end",
  notice_period_days: "Notice period",
};

function fmtValue(attr, v) {
  if (v == null) return "—";
  if (attr === "rate") return fmtCents(v);
  if (attr === "fte_hours") return `${Number(v).toFixed(1)}h/wk`;
  if (attr === "notice_period_days") return `${v} days`;
  if (attr === "site_home" || attr === "manager")
    return String(v).slice(0, 8);
  return String(v);
}

export default function AttributeHistoryPanel({ companyId, userId }) {
  const [history, setHistory] = React.useState(null);
  const [filterAttr, setFilterAttr] = React.useState("");

  React.useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(
          `/companies/${companyId}/hr/members/${userId}/history`,
        );
        setHistory(data.history || []);
      } catch (_) {
        setHistory([]);
      }
    })();
  }, [companyId, userId]);

  if (history === null) {
    return <div className="p-3 text-[12px] text-brand-muted">Loading…</div>;
  }
  if (history.length === 0) {
    return (
      <div className="p-3 text-[12px] text-brand-muted flex items-center gap-2">
        <History size={13} /> No history yet.
      </div>
    );
  }

  const attrs = Array.from(new Set(history.map((r) => r.attribute))).sort();
  const shown = filterAttr
    ? history.filter((r) => r.attribute === filterAttr)
    : history;

  return (
    <div data-testid="attribute-history-panel">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <History size={13} className="text-brand-violet" />
        <span className="micro-label">HISTORY</span>
        <div className="flex items-center gap-1 flex-wrap">
          <button
            onClick={() => setFilterAttr("")}
            className={`px-2 py-0.5 text-[10.5px] rounded-md mono-num ${filterAttr === "" ? "bg-brand-violet text-white" : "text-brand-muted hover:bg-brand-canvas-2"}`}
            data-testid="attr-filter-all"
          >
            all
          </button>
          {attrs.map((a) => (
            <button
              key={a}
              onClick={() => setFilterAttr(a)}
              className={`px-2 py-0.5 text-[10.5px] rounded-md mono-num ${filterAttr === a ? "bg-brand-violet text-white" : "text-brand-muted hover:bg-brand-canvas-2"}`}
              data-testid={`attr-filter-${a}`}
            >
              {a}
            </button>
          ))}
        </div>
      </div>
      <div className="card-panel overflow-hidden">
        <table className="fable-table !text-[11.5px]">
          <thead>
            <tr>
              <th className="w-[110px]">Attribute</th>
              <th className="w-[100px]">From</th>
              <th className="w-[100px]">To</th>
              <th>Value</th>
              <th>Reason</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <tr key={r.id} data-testid={`history-row-${r.id}`}>
                <td className="font-medium text-brand-ink">
                  {ATTR_LABEL[r.attribute] || r.attribute}
                </td>
                <td className="mono-num text-brand-muted">{r.valid_from}</td>
                <td className="mono-num text-brand-muted">
                  {r.valid_to ? r.valid_to : <span className="text-emerald-800">current</span>}
                </td>
                <td className="mono-num">{fmtValue(r.attribute, r.value)}</td>
                <td className="text-brand-muted truncate max-w-[220px]" title={r.reason}>
                  {r.reason || "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
